import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const PUBLIC_IMAGES_BY_NAME = {
  'Mobília Solta': 'Mobília Solta1920x1080.png',
  'Decorações e Tecidos': 'Decorações e Tecidos 1920x1080.png',
  'Planejados': 'Planejados 1920x1080.png',
};

const STATIC_GROUPS = [
  { id: 'mobilia-solta', title: 'Mobília Solta', name: 'Mobília Solta', description: 'Sofás, mesas, cadeiras, camas e móveis de área externa', image: null, publicImage: PUBLIC_IMAGES_BY_NAME['Mobília Solta'], subcategories: [{ name: 'Sofás e Poltronas' }, { name: 'Mesas e Superfícies' }, { name: 'Área Externa e Fibra' }, { name: 'Cadeiras e Bancos' }, { name: 'Camas e Colchões' }] },
  { id: 'decoracoes-tecidos', title: 'Decorações e Tecidos', name: 'Decorações e Tecidos', description: 'Cortinas, tapetes, luminárias, almofadas e itens decorativos', image: null, publicImage: PUBLIC_IMAGES_BY_NAME['Decorações e Tecidos'], subcategories: [{ name: 'Cortinas e Tapetes' }, { name: 'Abajures e Luminárias' }, { name: 'Almofadas e Roupas de Cama' }, { name: 'Itens Decorativos e Vasos' }] },
  { id: 'planejados', title: 'Planejados', name: 'Planejados', description: 'Lojas de móveis planejados e seus portfólios', image: null, publicImage: PUBLIC_IMAGES_BY_NAME['Planejados'], subcategories: [{ name: 'Planejados' }] },
];

const Catalog = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [storeId, setStoreId] = useState('');
  const [sort, setSort] = useState('engagement');
  const [favorites, setFavorites] = useState(new Set());
  const { user } = useAuth();
  const navigate = useNavigate();
  const [imageErrors, setImageErrors] = useState({});
  const [subcategoryImageErrors, setSubcategoryImageErrors] = useState(new Set());

  const displayGroups = categoryGroups.length > 0 ? categoryGroups : STATIC_GROUPS;
  const selectedGroup = displayGroups.find((g) => g.id === selectedGroupId);

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadCategoryTree();
    loadStores();
    if (user) {
      loadFavorites();
    }
  }, [search, category, storeId, sort, user]);

  const loadProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (storeId) params.append('store_id', storeId);
      if (sort) params.append('sort', sort);

      const response = await api.get(`/products?${params}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const loadCategories = async () => {
    try {
      try {
        const response = await api.get('/categories/list/simple');
        setCategories(response.data);
      } catch {
        const response = await api.get('/products/categories/list');
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadCategoryTree = async () => {
    try {
      const response = await api.get('/categories/list/tree');
      const tree = (response.data || []).map((item) => {
        const subs = (item.subcategories || []).map((c) => ({ name: c.name, id: c.id, image_url: c.image_url }));
        return {
          id: item.id,
          title: item.name,
          name: item.name,
          description: item.description || null,
          image: item.image_url || null,
          publicImage: PUBLIC_IMAGES_BY_NAME[item.name] || null,
          subcategories: subs.length > 0 ? subs : [{ name: item.name }],
        };
      });
      setCategoryGroups(tree);
    } catch (error) {
      console.error('Erro ao carregar árvore de categorias:', error);
      setCategoryGroups([]);
    }
  };

  const loadStores = async () => {
    try {
      const response = await api.get('/stores');
      setStores(response.data);
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await api.get('/favorites');
      const favoriteIds = new Set(response.data.map(p => p.id));
      setFavorites(favoriteIds);
    } catch (error) {
      console.error('Erro ao carregar favoritos:', error);
    }
  };

  const toggleFavorite = async (productId, e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const isFavorite = favorites.has(productId);
      if (isFavorite) {
        await api.delete(`/favorites/${productId}`);
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
      } else {
        await api.post(`/favorites/${productId}`);
        setFavorites(prev => new Set([...prev, productId]));
      }
      loadProducts();
    } catch (error) {
      console.error('Erro ao favoritar:', error);
    }
  };

  return (
    <Box>
      {/* Banner – container limita largura para não esticar a imagem e perder qualidade */}
      <Box
        sx={{
          pt: { xs: 8, md: 10 },
          mb: 6,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, rgba(124, 58, 237, 0.06) 0%, transparent 100%)',
        }}
      >
        <Container maxWidth="xl" disableGutters sx={{ px: { xs: 0, sm: 2 } }}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL || ''}/banner1-0.png`}
            alt="Banner Apuro"
            loading="eager"
            decoding="async"
            sx={{
              display: 'block',
              width: '100%',
              height: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
            }}
          />
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mb: 4 }}>
        <Box sx={{ mb: 5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Buscar produtos"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'primary.main' }} />,
          }}
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Categoria</InputLabel>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            label="Categoria"
          >
            <MenuItem value="">Todas</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Loja</InputLabel>
          <Select
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            label="Loja"
          >
            <MenuItem value="">Todas</MenuItem>
            {stores.map((store) => (
              <MenuItem key={store.id} value={store.id}>
                {store.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Ordenar</InputLabel>
          <Select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            label="Ordenar"
          >
            <MenuItem value="engagement">Engajamento</MenuItem>
            <MenuItem value="newest">Mais Recentes</MenuItem>
            <MenuItem value="name">Nome</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* 3 grandes categorias com imagens (public) */}
      <Container maxWidth="xl" sx={{ mb: 4 }}>
        <Typography
          variant="subtitle1"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            mb: 2,
            letterSpacing: '0.02em',
          }}
        >
          Categorias
        </Typography>
        <Grid container spacing={3}>
          {displayGroups.map((group) => (
            <Grid item xs={12} md={4} key={group.id}>
              <Card
                component="button"
                onClick={() => {
                  setSelectedGroupId(selectedGroupId === group.id ? null : group.id);
                  setCategory('');
                }}
                sx={{
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textAlign: 'left',
                  border: '2px solid',
                  borderColor: selectedGroupId === group.id ? 'primary.main' : 'divider',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: (theme) => theme.shadows[4],
                  },
                }}
              >
                {imageErrors[group.id] ? (
                  <Box
                    sx={{
                      height: 200,
                      display: 'flex',
                      bgcolor: 'action.hover',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CategoryIcon sx={{ fontSize: 64, color: 'primary.main' }} />
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    height="200"
                    image={group.image || (group.publicImage ? `${process.env.PUBLIC_URL || ''}/${group.publicImage}` : '')}
                    alt={group.title}
                    sx={{ objectFit: 'cover' }}
                    onError={() => setImageErrors((prev) => ({ ...prev, [group.id]: true }))}
                  />
                )}
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {group.title}
                  </Typography>
                  {group.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {group.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Opções da categoria selecionada – mesmo estilo de card */}
        {selectedGroup && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Card
              component="button"
              onClick={() => setCategory('')}
              sx={{
                minWidth: 140,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: category === '' ? 'primary.main' : 'divider',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: 'primary.main', boxShadow: (t) => t.shadows[4] },
              }}
            >
              <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CategoryIcon sx={{ fontSize: 22, color: 'primary.main', opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Todas
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            {selectedGroup.subcategories.map((sub) => (
              <Card
                key={sub.name}
                component="button"
                onClick={() => setCategory(sub.name)}
                sx={{
                  minWidth: 140,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: category === sub.name ? 'primary.main' : 'divider',
                  transition: 'all 0.2s ease',
                  overflow: 'hidden',
                  '&:hover': { borderColor: 'primary.main', boxShadow: (t) => t.shadows[4] },
                }}
              >
                {sub.image_url && !subcategoryImageErrors.has(sub.name) ? (
                  <>
                    <CardMedia
                      component="img"
                      height="80"
                      image={sub.image_url}
                      alt={sub.name}
                      sx={{ objectFit: 'cover' }}
                      onError={() => setSubcategoryImageErrors((prev) => new Set([...prev, sub.name]))}
                    />
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {sub.name}
                      </Typography>
                    </CardContent>
                  </>
                ) : (
                  <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CategoryIcon sx={{ fontSize: 22, color: 'primary.main', opacity: 0.9 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {sub.name}
                      </Typography>
                    </Box>
                  </CardContent>
                )}
              </Card>
            ))}
          </Box>
        )}
      </Container>

      <Grid container spacing={3}>
        {products.map((product) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
            <Card
              sx={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column' }}
              onClick={() => navigate(`/product/${product.id}`)}
            >
              <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                {product.image_url ? (
                  <CardMedia
                    component="img"
                    height="280"
                    image={product.image_url}
                    alt={product.name}
                    sx={{
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 280,
                      backgroundColor: (theme) => theme.palette.background.default,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CategoryIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                  </Box>
                )}
                <IconButton
                  onClick={(e) => toggleFavorite(product.id, e)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: (theme) => favorites.has(product.id) ? theme.palette.primary.main : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: '#ffffff',
                      color: 'primary.main',
                    },
                  }}
                >
                  {favorites.has(product.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                </IconButton>
              </Box>
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Typography 
                  variant="h6" 
                  component="h2" 
                  sx={{ 
                    flexGrow: 1,
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontWeight: 600,
                    mb: 1,
                    minHeight: '3em',
                  }}
                >
                  {product.name}
                </Typography>
                <Chip 
                  label={product.category} 
                  size="small" 
                  sx={{ 
                    mb: 1.5,
                    backgroundColor: (theme) => theme.palette.background.default,
                    color: 'primary.main',
                    fontWeight: 500,
                  }} 
                />
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mt: 1,
                    mb: 2,
                    minHeight: '3em',
                    lineHeight: 1.6,
                  }}
                >
                  {product.description?.substring(0, 120) || 'Produto exclusivo de alto padrão.'}
                  {product.description && product.description.length > 120 && '...'}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  pt: 2,
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: 'primary.main',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {product.store_name}
                  </Typography>
                  {Number(product.engagement_score) > 0 && (
                    <Chip
                      label={`⭐ ${Number(product.engagement_score).toFixed(0)}`}
                      size="small"
                      sx={{
                        backgroundColor: (theme) => theme.palette.background.default,
                        fontSize: '0.7rem',
                        color: 'primary.main',
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {products.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Nenhum produto encontrado
          </Typography>
        </Box>
      )}
      </Container>
    </Box>
  );
};

export default Catalog;
